/**
 * "Send later" on a request card, plus the modal behind it.
 *
 * Sits next to Send and Reminder in the card footer. Two presets at 09:00 Berlin cover the common
 * cases (a reply written at 22:40 that should not land at 22:40; a Friday-afternoon reply for
 * Monday), and a free datetime field covers the rest. Times are BERLIN wall-clock — see
 * `scheduleReplyTime.ts`.
 *
 * ⚠️ **It takes the editor's LIVE text, not `item.draft`.** The recruiter's own edits exist only in
 * the contentEditable DOM, exactly as the send path reads them, and the AGG feedback marker is
 * stripped the same way — otherwise a scheduled reply would ship the `data-agg-feedback` span or,
 * worse, the pre-edit draft.
 *
 * **Disabled without the permanent Gmail connection.** Scheduling needs the proxy to hold a refresh
 * token; without one the route answers 409 and nothing could ever be sent. Saying so on the button
 * is better than offering a click that fails.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { berlinWallClockToIso } from '../rescheduleForm';
import { ModalActions } from './ModalActions';
import { formatSendAt, presetsFor } from './scheduleReplyTime';
import { ScheduleTimePicker } from './ScheduleTimePicker';
import { useScheduledSends } from './useScheduledSends';

export function ScheduleReplyModal({
  open,
  onClose,
  threadId,
  opportunityId,
  candidateName,
  getBody,
  gmailToken,
}: {
  open: boolean;
  onClose: () => void;
  threadId: string;
  /**
   * The candidate's Lever opportunity, so the proxy can resolve which posting they are on. The
   * pilot's posting gate judges a queued reply by that at send time; without it an armed gate
   * refuses the reply up front. Null for a sender not in Lever.
   */
  opportunityId: string | null;
  candidateName: string;
  /** Reads the editor at CONFIRM time, so edits made while the modal is open are included. */
  getBody: () => string;
  gmailToken: string | null;
}) {
  const { schedule } = useScheduledSends();
  const presets = presetsFor();
  const [value, setValue] = useState(presets[0].value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    if (!gmailToken) {
      setError('Connect Gmail first, then schedule.');
      return;
    }
    const sendAt = berlinWallClockToIso(value);
    if (!sendAt) {
      setError('Pick a date and time.');
      return;
    }
    const body = getBody();
    if (!body.trim()) {
      setError('The reply is empty.');
      return;
    }
    setSaving(true);
    try {
      const row = await schedule({ threadId, opportunityId, sendAt, body, gmailToken });
      /**
       * The toast NAMES the time, in the same words the strip uses. A picker is easy to misread by
       * an hour, and an unnamed "Scheduled." leaves that undiscovered until the mail arrives.
       */
      showToast({
        status: 'success',
        description: `Scheduled for ${formatSendAt(row.sendAt)}. Cancel or move it in the strip at the top.`,
      });
      onClose();
    } catch (err) {
      // Includes NeedsGmailGrant, whose message already says what to do.
      const message = (err as Error)?.message || 'Could not schedule the reply.';
      setError(message);
      // Also a toast: the inline line sits below the fold on a short window, and a click that
      // silently leaves the modal open reads as a dead button.
      showToast({ status: 'error', description: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,32rem)] gap-4 rounded p-6" data-no-toggle>
        <ModalHeader>
          <ModalTitle>Send later to {candidateName}</ModalTitle>
        </ModalHeader>
        <ScheduleTimePicker
          value={value}
          onChange={setValue}
          disabled={saving}
          error={error}
        />
        <ModalActions
          confirmLabel={saving ? 'Scheduling…' : 'Schedule'}
          confirmDisabled={saving || !value}
          onConfirm={() => void save()}
          onCancel={onClose}
        />
      </ModalContent>
    </Modal>
  );
}

/** The card's entry point. Renders inside containers that already carry data-no-toggle. */
export function ScheduleReplyButton({
  threadId,
  opportunityId,
  candidateName,
  getBody,
  gmailToken,
  disabledReason,
}: {
  threadId: string;
  opportunityId: string | null;
  candidateName: string;
  getBody: () => string;
  gmailToken: string | null;
  /** Set when scheduling cannot work at all — shown instead of opening a modal that would fail. */
  disabledReason?: string | null;
}) {
  const [open, setOpen] = useState(false);
  if (disabledReason) {
    return (
      <Button size="sm" variant="outline" disabled title={disabledReason} data-no-toggle>
        Send later
      </Button>
    );
  }
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-no-toggle>
        Send later
      </Button>
      {open && (
        <ScheduleReplyModal
          open
          onClose={() => setOpen(false)}
          threadId={threadId}
          opportunityId={opportunityId}
          candidateName={candidateName}
          getBody={getBody}
          gmailToken={gmailToken}
        />
      )}
    </>
  );
}
